import { useState } from 'react';
import { AppHeader } from '../components/AppHeader';
import { BottomNav } from '../components/BottomNav';
import { FilterChip } from '../components/FilterChip';
import { TagChip, MainTagChip } from '../components/TagChip';
import { PartnerLogoBadge } from '../components/PartnerLogoBadge';
import { ContactCard } from '../components/ContactCard';
import { EventCard } from '../components/EventCard';
import { Button, PrimaryButton, SecondaryButton } from '../components/Button';
import { Music, Award, Users, Download, Share2, Plus } from 'lucide-react';

export default function ComponentDemo() {
  const [activeTab, setActiveTab] = useState<'calendar' | 'evenimente' | 'parteneri' | 'cont'>('calendar');
  const [selectedFilters, setSelectedFilters] = useState<string[]>(['Muzică']);
  const [isLoading, setIsLoading] = useState(false);

  const toggleFilter = (filter: string) => {
    setSelectedFilters(prev =>
      prev.includes(filter)
        ? prev.filter(f => f !== filter)
        : [...prev, filter]
    );
  };

  const removeFilter = (filter: string) => {
    setSelectedFilters(prev => prev.filter(f => f !== filter));
  };

  const handleLoadingDemo = () => {
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 2000);
  };

  return (
    <div className="min-h-screen bg-background pb-24" style={{ fontFamily: 'var(--font-primary)' }}>
      {/* App Header */}
      <AppHeader
        title="Componente Demo"
        onMenuClick={() => console.log('Menu clicked')}
        onNotificationClick={() => console.log('Notifications clicked')}
        hasNotifications={true}
      />

      <div className="mx-auto max-w-7xl px-4 py-8 space-y-12">
        {/* Buttons Section */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold" style={{ color: '#3b0b86' }}>
            Butoane
          </h2>
          
          {/* Default State */}
          <div className="mb-6 rounded-xl border bg-white p-6">
            <h3 className="mb-4 text-lg font-medium">Stare Implicită</h3>
            <div className="flex flex-wrap gap-4">
              <PrimaryButton>Buton Primar</PrimaryButton>
              <PrimaryButton icon={Plus}>Cu Iconă Stânga</PrimaryButton>
              <PrimaryButton icon={Download} iconPosition="right">
                Cu Iconă Dreapta
              </PrimaryButton>
              <SecondaryButton>Buton Secundar</SecondaryButton>
              <SecondaryButton icon={Share2}>Cu Iconă</SecondaryButton>
            </div>
          </div>

          {/* Sizes */}
          <div className="mb-6 rounded-xl border bg-white p-6">
            <h3 className="mb-4 text-lg font-medium">Dimensiuni</h3>
            <div className="flex flex-wrap items-center gap-4">
              <PrimaryButton size="sm">Mic</PrimaryButton>
              <PrimaryButton size="md">Mediu</PrimaryButton>
              <PrimaryButton size="lg">Mare</PrimaryButton>
            </div>
          </div>

          {/* Loading State */}
          <div className="mb-6 rounded-xl border bg-white p-6">
            <h3 className="mb-4 text-lg font-medium">Stare Loading</h3>
            <div className="flex flex-wrap gap-4">
              <PrimaryButton loading={isLoading} onClick={handleLoadingDemo}>
                {isLoading ? 'Se încarcă...' : 'Click pentru Loading'}
              </PrimaryButton>
              <SecondaryButton loading>Se încarcă...</SecondaryButton>
            </div>
          </div>

          {/* Disabled State */}
          <div className="mb-6 rounded-xl border bg-white p-6">
            <h3 className="mb-4 text-lg font-medium">Stare Dezactivată</h3>
            <div className="flex flex-wrap gap-4">
              <PrimaryButton disabled>Buton Primar</PrimaryButton>
              <SecondaryButton disabled>Buton Secundar</SecondaryButton>
            </div>
          </div>

          {/* Full Width */}
          <div className="rounded-xl border bg-white p-6">
            <h3 className="mb-4 text-lg font-medium">Lățime Completă</h3>
            <PrimaryButton fullWidth icon={Plus}>
              Adaugă Eveniment
            </PrimaryButton>
          </div>
        </section>

        {/* Filter Chips Section */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold" style={{ color: '#3b0b86' }}>
            Filter Chips
          </h2>
          <div className="rounded-xl border bg-white p-6">
            <div className="flex flex-wrap gap-3">
              {['Toate', 'Muzică', 'Sport', 'Artă & Cultură', 'Workshop', 'Social'].map(filter => (
                <FilterChip
                  key={filter}
                  label={filter}
                  selected={selectedFilters.includes(filter)}
                  onToggle={() => toggleFilter(filter)}
                  onRemove={selectedFilters.includes(filter) ? () => removeFilter(filter) : undefined}
                />
              ))}
              <FilterChip label="Dezactivat" disabled />
            </div>
          </div>
        </section>

        {/* Tag Chips Section */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold" style={{ color: '#3b0b86' }}>
            Tag Chips
          </h2>
          
          {/* Regular Tags */}
          <div className="mb-6 rounded-xl border bg-white p-6">
            <h3 className="mb-4 text-lg font-medium">Tag-uri Standard</h3>
            <div className="flex flex-wrap gap-2">
              <TagChip label="Muzică" icon={Music} color="#3b0b86" />
              <TagChip label="Sport" color="#ff6417" />
              <TagChip label="Artă" color="#01ffff" />
              <TagChip label="Premii" icon={Award} color="#3b0b86" />
              <TagChip label="Dezactivat" disabled />
            </div>
          </div>

          {/* Main Tags */}
          <div className="rounded-xl border bg-white p-6">
            <h3 className="mb-4 text-lg font-medium">Main Tags (Culoare Specială)</h3>
            <div className="flex flex-wrap gap-2">
              <MainTagChip label="Eveniment Principal" />
              <MainTagChip label="Promoție" icon={Award} />
              <MainTagChip label="Trending" icon={Users} />
              <MainTagChip label="Dezactivat" disabled />
            </div>
          </div>
        </section>

        {/* Partner Logo Badges Section */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold" style={{ color: '#3b0b86' }}>
            Partner Logo Badges
          </h2>
          <div className="rounded-xl border bg-white p-6">
            {/* Sizes */}
            <div className="mb-6">
              <h3 className="mb-4 text-lg font-medium">Dimensiuni - Pătrat</h3>
              <div className="flex flex-wrap items-center gap-4">
                <PartnerLogoBadge name="Partner SM" size="sm" variant="square" />
                <PartnerLogoBadge name="Partner MD" size="md" variant="square" />
                <PartnerLogoBadge name="Partner LG" size="lg" variant="square" />
              </div>
            </div>

            {/* Circle Variant */}
            <div>
              <h3 className="mb-4 text-lg font-medium">Dimensiuni - Cerc</h3>
              <div className="flex flex-wrap items-center gap-4">
                <PartnerLogoBadge name="Partner SM" size="sm" variant="circle" />
                <PartnerLogoBadge name="Partner MD" size="md" variant="circle" />
                <PartnerLogoBadge name="Partner LG" size="lg" variant="circle" />
              </div>
            </div>
          </div>
        </section>

        {/* Contact Cards Section */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold" style={{ color: '#3b0b86' }}>
            Contact Cards
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <ContactCard
              name="Maria Popescu"
              phone="+40 123 456 789"
              email="maria.popescu@example.com"
              role="Coordonator Evenimente"
            />
            <ContactCard
              name="Ion Ionescu"
              phone="+40 987 654 321"
              email="ion.ionescu@example.com"
              role="Manager Parteneriate"
            />
            <ContactCard
              name="Elena Georgescu"
              email="elena@example.com"
              role="Comunicare"
            />
          </div>
        </section>

        {/* Event Cards Section */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold" style={{ color: '#3b0b86' }}>
            Event Cards
          </h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <EventCard
              title="Concert Rock în Cetate"
              date="25 MAR 2026"
              time="19:00"
              location="Cetatea Alba Iulia"
              mainTags={[
                { label: 'Eveniment Principal' },
                { label: 'Trending', icon: Users },
              ]}
              otherTags={[
                { label: 'Muzică', color: '#3b0b86' },
                { label: 'Live', color: '#ff6417' },
              ]}
              onCardClick={() => console.log('Card clicked')}
              onLike={() => console.log('Liked')}
            />

            <EventCard
              title="Workshop Design Grafic pentru Tineri"
              date="28 MAR 2026"
              time="14:00"
              location="Biblioteca Județeană Lucian Blaga"
              mainTags={[{ label: 'Gratuit' }]}
              otherTags={[
                { label: 'Workshop', color: '#01ffff' },
                { label: 'Educație', color: '#3b0b86' },
              ]}
              isLiked={true}
              onCardClick={() => console.log('Card clicked')}
            />

            <EventCard
              title="Maraton Alba Iulia 2026"
              date="02 APR 2026"
              time="08:00"
              location="Centrul Istoric"
              mainTags={[{ label: 'Competiție', icon: Award }]}
              otherTags={[
                { label: 'Sport', color: '#ff6417' },
                { label: 'Outdoor', color: '#3b0b86' },
              ]}
              disabled
              onCardClick={() => console.log('Card clicked')}
            />
          </div>
        </section>

        {/* Pressed State Demo */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold" style={{ color: '#3b0b86' }}>
            Stări Interactive
          </h2>
          <div className="rounded-xl border bg-white p-6">
            <p className="mb-4 text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
              Toate componentele au stări hover și active (pressed). Încercați să faceți click și să țineți apăsat pe butoane și carduri pentru a vedea efectele.
            </p>
            <div className="flex flex-wrap gap-4">
              <PrimaryButton>Apasă-mă</PrimaryButton>
              <SecondaryButton icon={Share2}>Distribuie</SecondaryButton>
            </div>
          </div>
        </section>
      </div>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
}